import {CommonModule} from '@angular/common';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {BadgeModule} from 'primeng/badge';
import {ChartModule} from 'primeng/chart';
import {AvatarModule} from 'primeng/avatar';
import {IconFieldModule} from 'primeng/iconfield';
import {InputIconModule} from 'primeng/inputicon';
import {ButtonModule} from 'primeng/button';
import {InputTextModule} from 'primeng/inputtext';
import {MenuModule} from 'primeng/menu';
import {TextareaModule} from 'primeng/textarea';
import {ToggleSwitchModule} from 'primeng/toggleswitch';
import {DialogService, DynamicDialogModule, DynamicDialogRef} from 'primeng/dynamicdialog';
import {WebSocketService} from '../../../shared/services/webscoket.service';
import {ChatComponent} from '../../components/chat/chat.component';
import {ConversationService} from '../../services/conversation.service';
import {
  Conversation,
  ConversationPriority,
  ConversationStatus,
  MessageDirection
} from "../../contracts/conversation.interface";
import {ChatInvitationComponent} from '../../components/chat-invitation/chat-invitation.component';
import {catchError, debounceTime, distinctUntilChanged, EMPTY, Subscription, take, throwError} from 'rxjs';
import {ChatNotSelectedComponent} from '../../components/chat-not-selected/chat-not-selected.component';
import {filter} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {MessageType, WsMessage, WSMessageType} from '../../contracts/message.interface';
import {NotificationService} from '../../../shared/services/notification.service';
import {Tooltip} from 'primeng/tooltip';
import {TakingLongPipe} from '../../../shared/pipes/taking-long.pipe';
import {ContactDisplayPipe} from '../../../shared/pipes/contact-display.pipe';
import {CONVERSATION_AVATAR_COLORS, CONVERSATION_AVATAR_MAP} from '../../../shared/constants/conversation-avatar-map';
import {
  ConversationInitialsComponent
} from '../../../shared/components/conversation-initials/conversation-initials.component';
import {AuthService} from '../../../auth/services/auth.service';
import {DialogAction} from '../../../shared/contracts/dialog.interface';
import {
  CreateConversationDialogComponent
} from '../dialogs/create-conversation-dialog/create-conversation-dialog.component';
import {
  ConversationsFilterTabsComponent
} from '../../components/conversations-filter-tabs/conversations-filter-tabs.component';

@Component({
  selector: 'kc-conversations-list',
  standalone: true,
  templateUrl: './conversations-list.component.html',
  styleUrls: ['./conversations-list.component.scss'],
  providers: [DialogService],
  imports: [
    CommonModule,
    RouterModule,
    ChartModule,
    ToggleSwitchModule,
    BadgeModule,
    FormsModule,
    AvatarModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    InputTextModule,
    MenuModule,
    TextareaModule,
    DynamicDialogModule,
    ChatComponent,
    ChatInvitationComponent,
    ChatNotSelectedComponent,
    Tooltip,
    TakingLongPipe,
    ContactDisplayPipe,
    TakingLongPipe,
    ConversationInitialsComponent,
    ReactiveFormsModule,
    ConversationsFilterTabsComponent
  ],
  host: {
    class: 'flex-1 h-full overflow-x-clip overflow-hidden flex flex-col lg:flex-row border border-surface rounded-2xl'
  },
})
export class ConversationsListComponent implements OnInit, OnDestroy {
  messageSearchControl = new FormControl<string>('');
  selectedConversation?: Conversation;
  conversations: Conversation[] = [];
  filteredConversations: Conversation[] = [];
  search: string = '';
  messageSearch?: string;
  isReadOnly: boolean = false;
  viewChat: boolean = false;

  toReadCount: number = 0;
  toAcceptCount: number = 0;

  filterDaLeggere = false;
  filterDaAccettare = false;

  activeTab: 'attive' | 'recenti' = 'attive';
  options = [
    {label: 'Attive', value: 'attive'},
    {label: 'Recenti', value: 'recenti'},
  ];
  // TODO do we need the hard coded number?
  activeChat?: number;

  private subscription?: Subscription;
  private _refCreateConversationDialog?: DynamicDialogRef;

  constructor(
    private webSocketService: WebSocketService,
    private conversationService: ConversationService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private dialogService: DialogService,
  ) {}

  openCreateConversationDialog() {
    this._refCreateConversationDialog = this.dialogService.open(
      CreateConversationDialogComponent,
      CreateConversationDialogComponent.DIALOG_CONFIG,
    );

    this._refCreateConversationDialog.onClose
      .pipe(filter(result => result?.action === DialogAction.OK))
      .subscribe(result => {
        this.conversationService.createManual(result.data).pipe(
          take(1),
          catchError((err: HttpErrorResponse) => {
            if (err.status === 409) {
              const firstName: string | null = err.error?.operator_first_name;
              const lastName: string | null = err.error?.operator_last_name;
              const message = firstName && lastName
                ? `Questa conversazione è attualmente in gestione dell'operatore ${firstName} ${lastName}.`
                : 'Questa conversazione è già attiva o in attesa di assegnazione.';
              this.notificationService.warning(message);
              return EMPTY;
            }
            return throwError(() => err);
          }),
        ).subscribe(() => {
          this.notificationService.success('Conversazione creata con successo.');
          this.loadConversations();
        });
      });
  }

  ngOnInit() {
    this.subscription = this.webSocketService.messages$
      .pipe(
        filter(msg =>
          msg.type === WSMessageType.CONVERSATION_CREATED ||
          msg.type === WSMessageType.CONVERSATION_UPDATED ||
          msg.type === WSMessageType.CHAT ||
          msg.type === WSMessageType.CONVERSATION_SESSION_CHANGED
        ),
        filter(msg => !msg.isErrorMessage)
      )
      .subscribe(msg => {
        switch (msg.type) {
          case WSMessageType.CONVERSATION_CREATED:
          case WSMessageType.CONVERSATION_UPDATED:
            this.handleConversationUpdate(msg);
            break;
          case WSMessageType.CHAT:
            this.handleChatMessage(msg);
            break;
          case WSMessageType.CONVERSATION_SESSION_CHANGED:
            this.handleSessionChange(msg);
            break;
        }
      });

    this.loadConversations();

    this.messageSearchControl.valueChanges
      .pipe(
        debounceTime(1000),
        distinctUntilChanged()
      )
      .subscribe((value: any) => {
        this.messageSearch = value;
        if (this.activeTab === 'attive') {
          this.loadConversations(value);
        } else {
          this.loadRecents(value);
        }
      });
  }

  handleChoice(choice: boolean) {
    this.conversationService.respondToAssignment(this.selectedConversation!.id, choice).pipe(take(1)).subscribe(() => {
      this.loadConversations();
    });
  }

  onTabChange() {
    this.selectedConversation = undefined;
    this.conversations = [];
    this.filteredConversations = [];
    this.filterDaLeggere = false;
    this.filterDaAccettare = false;
    this.messageSearchControl.setValue('', { emitEvent: false });
    this.messageSearch = undefined;
    if (this.activeTab === 'attive') {
      this.loadConversations();
    } else {
      this.loadRecents();
    }
  }

  loadConversations(messageSearch?: string) {
    this.conversationService
      .getAll(messageSearch)
      .pipe(take(1))
      .subscribe(data => {
        this.conversations = data.data as Conversation[];
        this.applyFilters();
        this.selectedConversation = this.conversations.find(c => c.id === this.selectedConversation?.id);
      });
  }

  loadRecents(messageSearch?: string) {
    this.conversationService.getRecents(messageSearch)
      .pipe(take(1))
      .subscribe(data => {
        this.conversations = data;
        this.applyFilters();
        this.selectedConversation = this.conversations.find(c => c.id === this.selectedConversation?.id);
      });
  }

  closeConversation(closedConversation: Conversation) {
    this.loadConversations();
  }

  onUnselectConversation(): void {
    const currentUserId = this.authService.getCurrentUser()!.id;
    if (
      this.selectedConversation &&
      Array.isArray(this.selectedConversation.userIds) &&
      !this.selectedConversation.userIds.includes(currentUserId!)
    ) {
      this.filteredConversations = this.filteredConversations.filter(
        c => c.id !== this.selectedConversation!.id
      );
    }
    this.selectedConversation = undefined;
  }

  onUnselectConversationAndReopen() {
    this.selectedConversation = undefined;
    this.activeTab = 'attive';
    this.loadConversations();
  }

  selectConversation(conversation: Conversation, event: Event) {
    if (this.activeTab === 'recenti') {
      this.selectConversationAsReadOnly(conversation, event);
      return;
    }
    this.viewChat = false;
    this.isReadOnly = false;

    this.selectedConversation = conversation;
    this.activeChat = conversation.id;
    this.selectedConversation.notificationCount = 0;
    this.applyFilters();

    this.conversationService.readConversation(conversation.id).pipe(take(1)).subscribe(() => {});
  }

  selectConversationAsReadOnly(conversation: Conversation, event: Event) {
    this.viewChat = true;
    this.isReadOnly = true;

    console.log(this.viewChat, this.isReadOnly)

    this.selectedConversation = conversation;
    this.activeChat = conversation.id
    this.selectedConversation.notificationCount = 0;
    this.applyFilters();

    this.conversationService.readConversation(conversation.id).pipe(take(1)).subscribe(data => {})

    event.stopPropagation();
  }

  onMessageSearchChange(event: any) {
    this.messageSearch = event.target.value;
    if (this.activeTab === 'attive') {
      this.loadConversations(this.messageSearch);
    }
  }

  /**
   * Handles an incoming chat message (event: "chat").
   * Updates the conversation's last-message preview and increments the unread
   * counter for any conversation that is not currently open in the chat view.
   */
  private handleChatMessage(wsMessage: WsMessage): void {
    const conversation = this.conversations.find(c => c.id === wsMessage.content.conversationId);
    if (!conversation) return;

    conversation.lastMessage = wsMessage.content.text;
    if (wsMessage.content.type === MessageType.SENT) {
      conversation.lastMessageDirection = MessageDirection.OUTGOING;
    }
    if (!this.selectedConversation || conversation.id !== this.selectedConversation.id) {
      conversation.notificationCount = (conversation.notificationCount ?? 0) + 1;
    }

    this.applyFilters();
  }

  applyFilters() {
    let list = [...this.conversations];

    if (this.search !== '') {
      const searchLower = this.search.toLowerCase();
      list = list.filter((conversation: Conversation) =>
        conversation.contact?.lastName?.toLowerCase().includes(searchLower) ||
        conversation.contact?.firstName?.toLowerCase().includes(searchLower) ||
        conversation.fromNumber?.toLowerCase().includes(searchLower)
      );
    }

    this.toReadCount = this.conversations.filter((c: Conversation) =>
      (c.notificationCount ?? 0) > 0 && c.status !== ConversationStatus.ASSIGNED_PENDING
    ).length;

    this.toAcceptCount = this.conversations.filter((c: Conversation) =>
      c.status === ConversationStatus.ASSIGNED_PENDING
    ).length;

    if (this.activeTab === 'attive' && (this.filterDaLeggere || this.filterDaAccettare)) {
      list = list.filter(c => {
        const isDaLeggere = (c.notificationCount ?? 0) > 0 && c.status !== ConversationStatus.ASSIGNED_PENDING;
        const isDaAccettare = c.status === ConversationStatus.ASSIGNED_PENDING;
        return (this.filterDaLeggere && isDaLeggere) || (this.filterDaAccettare && isDaAccettare);
      });
    }

    this.filteredConversations = list;
  }

  onTabChangeFromChild(tab: 'attive' | 'recenti') {
    this.activeTab = tab;
    this.onTabChange();
  }

  onFilterToggle(key: 'da-leggere' | 'da-accettare') {
    if (key === 'da-leggere') this.filterDaLeggere = !this.filterDaLeggere;
    else this.filterDaAccettare = !this.filterDaAccettare;
    this.applyFilters();
  }

  /**
   * Handles conversation.created and conversation.updated events.
   *
   * Determines whether the incoming conversation should be visible in this user's
   * working list, then adds, updates, or removes it accordingly. Toast notifications
   * inform the user when a conversation is pushed out of their scope:
   *   – newly assigned to me  → info toast
   *   – revoked               → warning toast
   *   – taken by another      → info toast
   *   – closed                → info toast
   */
  private handleConversationUpdate(wsMessage: WsMessage): void {
    if (this.activeTab === 'recenti') return;

    const incoming: Conversation = wsMessage.content;
    const myId = this.authService.getCurrentUser()!.id!;
    const idx = this.conversations.findIndex(c => c.id === incoming.id);
    const exists = idx !== -1;
    const isSelected = this.selectedConversation?.id === incoming.id;
    const shouldBeVisible = this.isConversationVisibleForMe(incoming, myId);

    if (!shouldBeVisible) {
      if (exists) {
        this.conversations.splice(idx, 1);
        if (isSelected) this.selectedConversation = undefined;
        this.applyFilters();
        this.toastForRemovedConversation(incoming, myId);
      }
      return;
    }

    const isNewlyAssignedToMe =
      !exists &&
      incoming.assignment?.assignedUserId === myId &&
      incoming.status === ConversationStatus.ASSIGNED_PENDING;

    if (!exists) {
      this.conversations.unshift(incoming);
    } else {
      this.conversations[idx] = { ...this.conversations[idx], ...incoming };
    }

    if (isSelected) {
      this.selectedConversation = this.conversations.find(c => c.id === incoming.id);
    }

    if (isNewlyAssignedToMe) {
      this.notificationService.info('Nuova conversazione assegnata', 'Avviso');
    }

    this.applyFilters();
  }

  /**
   * Returns true if the conversation should appear in the current user's list.
   *
   * Visible when:
   *   1. Assigned to the current user (any active status)
   *   2. TO_ASSIGN and the backend confirms the user is contact-linked via userIds
   *      (operators directly assigned to the contact + members of contact's teams).
   *
   * The fallback `this.conversations.some(...)` handles conversations that were
   * already loaded via REST API, where userIds is null. WS payloads always
   * include userIds, so the fallback is only reached on REST-loaded items.
   */
  private isConversationVisibleForMe(conversation: Conversation, myId: number): boolean {
    if (conversation.status === ConversationStatus.CLOSED) return false;
    if (conversation.assignment?.assignedUserId === myId) return true;

    if (conversation.status === ConversationStatus.TO_ASSIGN) {
      const contactLinked = conversation.userIds?.includes(myId);
      return contactLinked ?? this.conversations.some(c => c.id === conversation.id);
    }

    return this.conversations.some(c => c.id === conversation.id);
  }

  /**
   * Emits an appropriate toast when a conversation is removed from the current
   * user's list, based on the new status and assignment.
   */
  private toastForRemovedConversation(conversation: Conversation, myId: number): void {
    if (conversation.status === ConversationStatus.CLOSED) {
      this.notificationService.info('Conversazione chiusa', 'Avviso');
    } else if (conversation.status === ConversationStatus.TO_ASSIGN) {
      this.notificationService.warning('Conversazione revocata', 'Avviso');
    } else {
      this.notificationService.info('Conversazione assegnata ad un altro operatore', 'Avviso');
    }
  }

  /**
   * Handles the 24-hour messaging window status change (event: "conversation.session_changed").
   * Updates the isWindowExpired flag on the affected conversation and the selected conversation.
   */
  private handleSessionChange(wsMessage: WsMessage): void {
    const conversation = this.conversations.find(c => c.id === wsMessage.content.id);
    if (conversation) {
      conversation.isWindowExpired = wsMessage.content.isWindowExpired;
    }
    if (this.selectedConversation?.id === wsMessage.content.id) {
      this.selectedConversation!.isWindowExpired = wsMessage.content.isWindowExpired;
    }
  }

  ngOnDestroy() {
    // IMPORTANT: unsubscribe to prevent memory leaks & unnecessary processing
    this.subscription?.unsubscribe();
  }

  priorities = [
    { label: 'Nessuna', value: ConversationPriority.DEFAULT },
    { label: 'Bassa', value: ConversationPriority.LOW },
    { label: 'Media', value: ConversationPriority.MEDIUM },
    { label: 'Alta', value: ConversationPriority.HIGH },
  ];

  getPriorityLabel(priority: ConversationPriority): string {
    return this.priorities.find(p => p.value === priority)?.label ?? '';
  }

  getPriorityIcon(priority: ConversationPriority): string {
    const map: Record<ConversationPriority, string> = {
      [ConversationPriority.HIGH]:    'fa-duotone fa-solid fa-signal-bars',
      [ConversationPriority.MEDIUM]:  'fa-duotone fa-solid fa-signal-bars-fair',
      [ConversationPriority.LOW]:     'fa-duotone fa-solid fa-signal-bars-weak',
      [ConversationPriority.DEFAULT]: 'fa-duotone fa-solid fa-signal-bars-slash',
    };
    return map[priority] ?? 'fa-duotone fa-solid fa-signal-bars-slash';
  }

  protected readonly ConversationStatus = ConversationStatus;
  protected readonly ConversationPriority = ConversationPriority;
}
